/**
 * Query parameter used in a route as an additional parameter,
 * e.g. QueryParam.Sdg is used in a project market to select an sdg for the sdg-filter.
 */
export enum QueryParam {
  Sdg = "sdg",
  Category = "category",
  Classification = "classification"
}

export const CHALLENGE_PATH_PARAM = "challengeSlugOrId"

/**
 * To avoid typos and to allow changing the URLs later all links should use this enum
 * to organize existing pages.
 */
export enum Routes {
  // #region public pages

  Home = "/",
  About = "/about",
  Contact = "/contact",
  DataProtection = "/data-protection",
  Netiquette = "/netiquette",
  TermsOfUse = "/terms-of-use",
  Imprint = "/imprint",
  Maintenance = "/maintenance",
  ChallengeView = `/challenge/[${CHALLENGE_PATH_PARAM}]`,

  // #region Tools
  FAQ = "/tools/faq",
  Pubtools = "/tools/pubtools",
  // #endregion

  // #region user handling
  ConfirmAccount = "/user/confirm-account/[id]/[token]",
  ConfirmEmailChange = "/user/confirm-email/[id]/[token]",
  ConfirmPasswordReset = "/user/reset-password/[id]/[token]",
  ForgotPassword = "/user/forgot-password",
  Login = "/user/login",
  Registration = "/user/register",
  OAuthVerification = "/user/oauth-verification",
  // #endregion

  // #region public project data/pages
  MarketOfCategories = "/market/[id]/categories",
  MarketOfIdeas = "/market/[id]/ideas",
  MarketOfProjects = "/market/[id]/projects",
  MarketOfPrograms = "/market",
  MarketOfSupportRequests = "/market/[id]/support-requests",
  ProjectPage = "/project/[slug]", // also works for ID
  CreateIdea = "/market/[id]/create-idea",
  ChooseProgramToCreateIdea = "/market/create-idea",
  CreateProjectFromIdea = "/market/[id]/ideas/[ideaId]/create-project",
  CreateProject = "/market/[id]/create-project",
  ProjectApplication = "/project/[slug]/apply",
  // #endregion

  // #endregion public pages

  // #region user area, restricted for logged in users
  UserDashboard = "/user/dashboard",
  Feedback = "/user/feedback",
  ImportProject = "/user/import-project",
  MySupportOffers = "/user/supportoffers",
  MyProjects = "/user/projects",
  SingleDiscussionPage = "/user/feedback/[id]",
  // #endregion user area, restricted for logged in users

  // #region project pages for project team members
  ProjectProfile = "/project/[slug]/profile",
  ProjectProfileEdit = "/project/[slug]/profile/edit",
  ProjectSelectChallenge = "/project/[slug]/proposal/select-challenge",
  ProjectFeedbackDashboard = "/project/[slug]/feedback",
  ProjectFeedbackPage = "/project/[slug]/feedback/feedback",
  ProjectMap = "/project/[slug]/profile/map",
  ProjectMembers = "/project/[slug]/team/members",
  ProjectNetworkDashboard = "/project/[slug]/network",
  ProjectNetworkCreateSupportRequest = "/project/[slug]/network/supportrequests/create",
  ProjectNetworkSupportRequests = "/project/[slug]/network/supportrequests",
  ProjectNetworkSupportOffersOfOneSupportRequest = "/project/[slug]/network/supportrequests/[id]/supportoffers",
  ProjectPartners = "/project/[slug]/plan/partners",
  ProjectOwnContributions = "/project/[slug]/plan/own-contributions",
  ProjectPlan = "/project/[slug]/plan",
  ProjectPlanDescription = "/project/[slug]/plan/description",
  ProjectPlanTargetgroupsImpact = "/project/[slug]/plan/targetgroups-impact",
  ProjectPlanTasks = "/project/[slug]/plan/tasks",
  ProjectPlanWorkPackages = "/project/[slug]/plan/work-packages",
  ProjectPlanTimetable = "/project/[slug]/plan/timetable",
  ProjectPlanResourceRequirements = "/project/[slug]/plan/resource-requirements",
  ProjectPlanResourceCostCategories = "/project/[slug]/plan/cost-categories?proposal=[proposal]",
  ProjectPlanFinances = "/project/[slug]/plan/finances",
  ProjectProposals = "/project/[slug]/proposal",
  ProjectTeamMeeting = "/project/[slug]/team/teammeeting",
  ProjectTeamUpload = "/project/[slug]/team/team-uploads",
  ProjectSummary = "/project/[slug]/project-summary",
  ProjectWorkshop = "/project/[slug]/workshop",

  // Documents area
  ProjectDocuments = "/project/[slug]/documents",
  ProjectDocumentEdit = "/project/[slug]/document/[documentId]",

  // FinancePlan area
  ProjectFinancePlanOverview = "/project/[slug]/finance-plan",
  ProjectFinancePlan = "/project/[slug]/finance-plan/[financePlanId]",
  // #endregion

  // #region tenant area
  TenantCreate = "/tenant/create",
  TenantCreateChallenge = "/tenant/[id]/create-challenge?type=[type]",
  TenantCreateProgram = "/tenant/[id]/create-program",
  TenantDashboard = "/tenant/[id]/dashboard",
  TenantEdit = "/tenant/[id]/edit",
  TenantChallengeView = `/tenant/[id]/challenge/[${CHALLENGE_PATH_PARAM}]/details`,
  TenantProgramCategories = "/tenant/[id]/program/[programId]/categories",
  TenantProgramCategoriesAdd = "/tenant/[id]/program/[programId]/categories/add",
  TenantProgramCategoriesEdit = "/tenant/[id]/program/[programId]/categories/[category]/edit",
  TenantProgramEdit = "/tenant/[id]/program/[programId]/edit",
  TenantView = "/tenant/[id]",
  // #endregion

  // #region program area
  ProgramDashboard = "/program/[id]/dashboard",
  CommunityManagerProgramCategories = "/program/[id]/categories",
  ProgramView = "/program/[id]",
  ProgramProjects = "/program/[id]/projects",
  ProgramIdeas = "/program/[id]/ideas",
  ManagerProjectDetails = "/program/[id]/project/[projectId]",

  // @todo multi -> remove when useCurrentProgram() is obsolete
  SingleMultiCurrentProgram = "/program",
  // #endregion

  // #region platform manager area
  PlatformManagerDashboard = "/platform/manage",
  PlatformSystemOverview = "/platform/system",
  PlatformSystemIcons = "/platform/icons",
  PlatformPageAccessOverview = "/platform/page-access",
  // #endregion

  // #region basic administrative pages for process manager / admin
  // @todo multi: überarbeiten
  ManagerUserSearch = "/user/manage",
  ManagerUserDetails = "/user/manage/[id]",
  /** @deprecated ersetzen durch das jeweilige Manager-Dashboard */
  AdminDashboard = "/management",

  /** @deprecated ersetzen durch ProgramProjects oder Managerrollen-spezifische Project-Pages, siehe https://futureprojects.atlassian.net/browse/FCP-1738 */
  AdminProjectSearch = "/management/project",
  /** @deprecated */
  AdminProjectProfile = "/management/project/[id]/profile",
  // #endregion

  // #region challenge/fund management
  ChallengeDashboard = "/challenge/dashboard",
  ChallengeDetailsEdit = `/challenge/[${CHALLENGE_PATH_PARAM}]/details/edit?type=[type]`,
  // TODO challenge Redirect for SINN to https://staging.sinn-sachsen.de/portal/organisation/[organization-ID]/offers/[offer-ID]/edit/general
  ChallengeDetailsView = `/challenge/[${CHALLENGE_PATH_PARAM}]/details?type=[type]`,
  ChallengeProcedureAndTimelineEdit = `/challenge/[${CHALLENGE_PATH_PARAM}]/procedure-timeline/edit?type=[type]`,
  ChallengeProcedureAndTimelineView = `/challenge/[${CHALLENGE_PATH_PARAM}]/procedure-timeline?type=[type]`,
  ChallengeProposalDefintionsView = `/challenge/[${CHALLENGE_PATH_PARAM}]/proposal-definition?type=[type]`,
  ChallengeViewProposals = `/challenge/[${CHALLENGE_PATH_PARAM}]/view-proposals?type=[type]`,
  /** @deprecated Migrieren und verschieben unter /challenge/[challengeId] */
  AdminChallengeSelectProposals = "/management/challenges/[id]/select-proposals",
  /** @deprecated Migrieren und verschieben unter /challenge/[challengeId] */
  AdminChallengeGrant = "/management/challenges/[id]/grant",
  /** @deprecated Migrieren und verschieben unter /challenge/[challengeId] */
  AdminChallengeGrantEdit = "/management/challenges/[id]/grant/edit",
  // #endregion challenges/fund workflow

  // #region feedback for management
  // @todo: in communitymanager folder verschieben
  AdminFeedbackInvitationTimeline = "/management/feedback/invitation/[id]/timeline",
  AdminFeedbackInvitationActivate = "/management/feedback/invitation/[id]/activate",
  AdminFeedbackInvitationCreate = "/management/feedback/invitation/create",
  AdminFeedbackInvitationEdit = "/management/feedback/invitation/[id]/edit",
  AdminFeedbackInvitationResults = "/management/feedback/invitation/[id]/results",
  AdminFeedbackInvitations = "/management/feedback/invitation",
  AdminFeedbackInvitationView = "/management/feedback/invitation/[id]",
  // #endregion

  // download-page for files/pdf
  DownloadTriggerPage = "/download/[type]/[id]",
}

/**
 * Routes to the backend, especially to download files
 */
export enum BackendRoutes {
  DocumentProposalAttachmentDownload = "/document_proposal_attachments/[id]/download",
  FileProposalAttachmentDownload = "/file_proposal_attachments/[id]/download",
  /** @deprecated does not exist anymore */
  ProposalAttachmentDownload = "/proposal_attachments/[id]/download",
  ProposalPdfDownload = "/proposals/[id]/proposal-pdf-download",
  ProposalTeamPdfDownload = "/proposals/[id]/team-pdf-download",
  ProjectPdfDownload = "/projects/[id]/download-pdf",
  ProjectPlanProposalAttachmentDownload = "/project_plan_proposal_attachments/[id]/download",
  TeamUploadDownload = "/team_uploads/[id]/download",
}